import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  listUsers,
  createUser,
  bulkImportUsers,
  type User,
  type CreateUserData,
  type UpdateUserData,
  type BulkImportUser,
  type BulkImportResult,
} from '../../api/users';
import { DataTable, type Column } from '../common/DataTable';
import { StatusBadge } from '../common/StatusBadge';
import { Modal } from '../common/Modal';
import { UserForm } from './UserForm';

// ── CSV helpers ────────────────────────────────────────────────────────────

function splitCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

function parseCSV(text: string): BulkImportUser[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];

  const headers = splitCSVLine(lines[0]).map((h) => h.toLowerCase().replace(/[^a-z0-9]/g, ''));
  const users: BulkImportUser[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = splitCSVLine(lines[i]);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = (values[idx] ?? '').trim();
    });

    // Support both camelCase and lowercase header names
    const username    = row.username    ?? '';
    const email       = row.email       ?? '';
    const displayName = row.displayname ?? row.displayName ?? row.name ?? '';
    const role        = row.role        ?? '';
    const maxBudget   = row.maxbudget   ?? row.maxBudget   ?? row.budget ?? '';

    if (!username && !email) continue; // blank row

    users.push({
      username,
      email,
      displayName,
      role:      role      || undefined,
      maxBudget: maxBudget || undefined,
    });
  }
  return users;
}

function downloadTemplate() {
  const csv = [
    'username,email,displayName,role,maxBudget',
    'jsmith,john.smith@example.com,John Smith,user,100',
    'aadmin,alice@example.com,Alice Administrator,admin,',
  ].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'users-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ──────────────────────────────────────────────────────────────

export function UsersView() {
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [users, setUsers]             = useState<User[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [showAddModal, setShowAddModal]     = useState(false);
  const [importing, setImporting]           = useState(false);
  const [importResult, setImportResult]     = useState<BulkImportResult | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listUsers(token);
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (data: CreateUserData | UpdateUserData) => {
    const token = await getJwtToken();
    if (!token) return;
    await createUser(data as CreateUserData, token);
    setShowAddModal(false);
    await fetchUsers();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so the same file can be re-selected

    const text   = await file.text();
    const parsed = parseCSV(text);

    if (parsed.length === 0) {
      setError('No valid rows found in CSV. Download the template to see the expected format.');
      return;
    }

    const token = await getJwtToken();
    if (!token) return;

    setImporting(true);
    try {
      const result = await bulkImportUsers(parsed, token);
      setImportResult(result);
      if (result.created.length > 0) await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const columns: Column<User>[] = [
    { key: 'username', header: 'Username' },
    { key: 'email', header: 'Email' },
    {
      key: 'status',
      header: 'Status',
      render: (u) => <StatusBadge status={u.status === 'active' ? 'Active' : 'Disabled'} />,
    },
    { key: 'role', header: 'Role' },
    {
      key: 'maxBudget',
      header: 'Budget',
      render: (u) => {
        if (u.maxBudget == null) return <span style={{ color: 'var(--text-muted)' }}>No limit</span>;
        const spent = u.spend ?? 0;
        const pct = Math.min((spent / u.maxBudget) * 100, 100);
        const tier = pct >= 80 ? 'red' : pct >= 50 ? 'yellow' : 'green';
        return (
          <div className="budget-indicator">
            <span className={`budget-indicator__label budget-indicator__label--${tier}`}>
              ${spent.toFixed(2)} / ${u.maxBudget.toFixed(2)}
            </span>
            <div className="budget-bar">
              <div
                className={`budget-bar__fill budget-bar__fill--${tier}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (u) => new Date(u.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Users</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + Add User
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            type="button"
          >
            {importing ? 'Importing…' : '↑ Import CSV'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={downloadTemplate}
            type="button"
          >
            ↓ Template
          </button>
          <button
            className="btn btn--secondary"
            onClick={fetchUsers}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Hidden file input for CSV upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && users.length === 0 ? (
        <div className="view__empty">Loading users...</div>
      ) : (
        <DataTable<User>
          columns={columns}
          data={users}
          keyExtractor={(u) => u.username}
          onRowClick={(u) => navigate(`/admin/users/${u.username}`)}
          emptyMessage="No users found. Click 'Add User' to create one."
        />
      )}

      {/* Add single user modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add User"
        wide
      >
        <UserForm
          onSubmit={handleCreate}
          onCancel={() => setShowAddModal(false)}
        />
      </Modal>

      {/* Bulk import results modal */}
      <Modal
        isOpen={importResult !== null}
        onClose={() => setImportResult(null)}
        title="Import Results"
      >
        {importResult && <ImportResultsView result={importResult} />}
      </Modal>
    </div>
  );
}

// ── Import results sub-component ───────────────────────────────────────────

function ImportResultsView({ result }: { result: BulkImportResult }) {
  const { created, skipped, errors } = result;
  const total = created.length + skipped.length + errors.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Summary row */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <StatPill value={created.length} label="created" color="var(--color-success, #22c55e)" />
        <StatPill value={skipped.length} label="skipped" color="var(--color-warning, #f59e0b)" />
        <StatPill value={errors.length}  label="errors"  color="var(--color-error,   #ef4444)" />
        <StatPill value={total}          label="total"   color="var(--text-muted,    #6b7280)" />
      </div>

      {skipped.length > 0 && (
        <div className="alert alert--error" style={{ background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.3)' }}>
          <strong>Skipped (already exist):</strong> {skipped.join(', ')}
        </div>
      )}

      {errors.length > 0 && (
        <div>
          <div className="alert alert--error" style={{ marginBottom: '8px' }}>
            {errors.length} row{errors.length !== 1 ? 's' : ''} had errors and were not imported.
          </div>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Row</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Username</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {errors.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '6px 8px' }}>{e.row}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{e.username || '—'}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--color-error, #ef4444)' }}>{e.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {created.length > 0 && errors.length === 0 && skipped.length === 0 && (
        <div className="alert" style={{ background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)', border: '1px solid' }}>
          All {created.length} user{created.length !== 1 ? 's' : ''} imported successfully.
        </div>
      )}
    </div>
  );
}

function StatPill({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '64px' }}>
      <span style={{ fontSize: '24px', fontWeight: 600, color }}>{value}</span>
      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{label}</span>
    </div>
  );
}
